import React, { useEffect,  useState, useRef } from 'react'
import { useSelector, useDispatch, batch } from 'react-redux'
import {
    DatePicker, Input, Select, message
} from 'antd';

// Components
import Page from '../../components/Decoration/Page'
import VehiclePlateECUTag from '../../components/VehiclePlateECUTag'
import showSecondarySidebar from '../../layout/MainLayout/helpers/showSecondarySidebar';
import UtilizationExtras from './components/UtilizationExtras';
import {
    SearchAdvanced,
    SearchFields,
    TabulatedReport
} from './components'
import { PrimaryButton } from '../../components/Decoration/Button';

// Constant
import { TEMPLATE } from '../../constants';

// Handler
import { xlsx_handler } from '../../handlers'

// Service
import { TIME, REDUX } from '../../services/util';

// Redux Action
import * as ACTIONS from '../../services/redux/actions'
import { getAddressHDLR, getLocAndVIDHDLR } from '../../handlers/address';

const {
    referenceEndTime,
    referenceStartTime,
    referenceEndOfDay,
    dayDiffABS,
    dayJSLocalize,
    parseExportTime,
    parseTime
} = TIME

const {
    REPORT
} = TEMPLATE

const {
    exportReportFile
} = xlsx_handler

const VehicleOverallReport = () => {

    const dispatch = useDispatch()

    const loadingVehicleReportState = useSelector(state => state.containers.v3.style.loading.vehicleReport)
    const vehicles = useSelector(state => state.containers.v3.vehicles.staticsByID)
    const geofences = useSelector(state => state.containers.v3.geofences.byID)

    const user = useSelector(REDUX.reduxStoreWrapper('user'))
    const { uroleid: curUserRole, uid, name } = user

    const apiUsage = useSelector(REDUX.reduxStoreWrapper('utils.api.usage.vehicleReport'))

    const geofenceGroups = useSelector(REDUX.reduxStoreWrapper('geofenceTemplates.byID'))
    const transitRecords = useSelector(REDUX.reduxStoreWrapper('transitRecords'))
    const tripRecords = useSelector(REDUX.reduxStoreWrapper('tripRecords'))
    const vehicleLogs = useSelector(REDUX.reduxStoreWrapper('vehicleLogs'))
    const vehicleDerivativeLogs = useSelector(REDUX.reduxStoreWrapper('vehicleDerivativeLogs'))
    const vehicleEventLogs = useSelector(REDUX.reduxStoreWrapper('vehicleEventLogs'))
    const vehicleGeoStateLogs = useSelector(REDUX.reduxStoreWrapper('vehicleGeoStateLogs'))
    const vehicleReportDailySummaries = useSelector(REDUX.reduxStoreWrapper('vehicleReportDailySummaries'))
    const vehicleRefuel = useSelector(REDUX.reduxStoreWrapper('vehicleReportRefuel'))
    const vehicleReportGeofences = useSelector(REDUX.reduxStoreWrapper('vehicleReportGeofences'))
    const vehicleUtilisation = useSelector(REDUX.reduxStoreWrapper('vehicleReport'))
    const vehicleFuelInfo = useSelector(REDUX.reduxStoreWrapper('vehicles.fuelProfileByID'))
    const coordinates = useSelector(REDUX.reduxStoreWrapper('locationTracking.byCoordinates'))
    const drivers = useSelector(REDUX.reduxStoreWrapper('drivers.byID'))
    const devicesMDVR = useSelector(REDUX.reduxStoreWrapper('devicesMDVR'))
    const users = useSelector(REDUX.reduxStoreWrapper('userHierarchy.byID'))

    const liveCamera = useSelector(REDUX.reduxStoreWrapper('liveCamera'))

    const [ VIDS, setVIDS ] = useState([])
    const [ GEOIDS, setGEOIDS ] = useState([])
    const [ startTime, setStartTime ] = useState(referenceStartTime())
    const [ endTime, setEndTime ] = useState(referenceEndTime())
    const [ selectedTable, setSelectedTable ] = useState(REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.TRIP)
    const [ filteredDataSrc, setFilteredDataSrc ] = useState([])
    const [ filterPrice, setFilterPrice ] = useState(2.95)
    // const [ litrePerKM, setLitrePerKM ] = useState(0.093)
    const [ durations, setDurations ] = useState('All')

    const searchTextState = useState('')
    const searchedColumnState = useState('')

    const locForAddress = (param) => (
        (
            (param === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.TRIP) && Object.values(tripRecords.recordByVID)?.flat()
        ) 
        || 
        (
            (param === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.TRANSIT) && Object.values(transitRecords.recordByVID)?.flat()
        )
    ) || []

    getAddressHDLR(getLocAndVIDHDLR(locForAddress(selectedTable)))

    const formatTableJSON = REPORT.VEHICLE_REPORT_TABLE({vehicles, geofences, drivers, FILTER_OUT_ROLE: TEMPLATE.USER_ROLE.FILTER_OUT_ROLE, userRole:curUserRole, users, userCam: liveCamera.user, filterPrice, searchTextState, searchedColumnState })

    const dataSubmitHandler = (dayDiff, max_query_window, check) => {

        // reset every report
        batch(() => {
            dispatch(ACTIONS.v3_vehicleLogs.clear_vehicle_event_log_request())
            dispatch(ACTIONS.v3_vehicleReports.clear_vehicle_utilisation_report_request())
            dispatch(ACTIONS.v3_devicesMDVR.unset_device_mdvr_success())
            dispatch(ACTIONS.v3_devicesMDVR.unset_passenger_count_success())
        })

        if(check) {
            if(dayDiff <= max_query_window) {	
                dispatch(ACTIONS.v3_cep.get_vehicle_report_multi_params_request(selectedTable, VIDS, startTime.utc().valueOf(), endTime.utc().valueOf(), uid, liveCamera.sessionID))
            } else {
                message.warning(`Please query the vehicle report with ${ max_query_window} days`)
            }

        } else {
            dispatch(ACTIONS.v3_cep.get_vehicle_report_multi_params_request(selectedTable, VIDS, startTime.utc().valueOf(), endTime.utc().valueOf(), uid, liveCamera.sessionID))
        }
    }

    const submitFormHandler = () => {

        const dayDiff = dayDiffABS(startTime, endTime)

        if(startTime.utc().valueOf() > endTime.utc().valueOf()) {
			message.warning(`Please query the vehicle report with end time larger than start time`)
			return 
		}

        if(REPORT.VEHICLE_REPORT_QUERY_LIMIT[selectedTable]) {
            dataSubmitHandler(dayDiff, REPORT.VEHICLE_REPORT_DAY_QUERY_WINDOW, true)
        } else {
            dataSubmitHandler(dayDiff, 0, false)
        }
    }

    const handleCalendarDisabledDate = (current) => current && current > referenceEndOfDay()

    const exportTitle = () => {
        // const vehiclePlates = VIDS.map(vid => vehicles[vid]?.plate).join('_')

        return `vehicle_${REPORT.VEHICLE_REPORT_FILE_NAME[selectedTable]}_${parseExportTime(startTime)}_${parseExportTime(endTime)}`
    }

    const exportDataHandler = () => {
        const col = 
            formatTableJSON[selectedTable].export
            &&
            formatTableJSON[selectedTable].export.filter(c => !c.isHidden)

        const exportReportInfo = [
            { label: 'Company Name', value: name },
            { label: 'Vehicle Plate', value: VIDS?.map(vid => vehicles[vid]?.plate)?.join(', ') },
            { label: 'Timeframe', value: `${parseTime(startTime)} - ${parseTime(endTime)}` },
        ]

        exportReportFile(filteredDataSrc, col, "Main", exportTitle(), exportReportInfo)
    }

    useEffect(() => {
        const newFilteredDataSrc = formatTableJSON[selectedTable]
            .dataSrcFormatter(
                {
                    vids: VIDS, 
                    geoids: GEOIDS,
                    geofenceGroups,
                    transitRecords, 
                    tripRecords, 
                    vehicleLogs, 
                    vehicleDerivativeLogs, 
                    vehicleEventLogs, 
                    vehicleGeoStateLogs, 
                    vehicleReportDailySummaries,
                    vehicleRefuel,
                    vehicleReportGeofences,
                    vehicleFuelInfo,
                    coordinates,
                    filterPrice,
                    // litrePerKM,
                    durations,
                    drivers,
                    vehicleUtilisation,
                    devicesMDVR,
                    vehicles,
                    users
                }
            )

        setFilteredDataSrc(newFilteredDataSrc)

    }, [apiUsage, coordinates])

    const dateValues = React.useMemo(() => {
        return [dayJSLocalize(startTime), dayJSLocalize(endTime)];
    }, [startTime, endTime]);

    
    // Load device/users
    const isLoad = useRef(false)
    const userHierarchyTree = useSelector(state => state.containers.v3.userHierarchy.hierarchyTree)

    useEffect(() => {
        if(uid && !isLoad.current && !Object.keys(userHierarchyTree).length) {
            dispatch(ACTIONS.v3_userHierarchy.user_hierarchy_get_request(user, user.hierCode))
            isLoad.current = true
        }
    }, [uid])
    

    const bulkDownloadHDLR = () => {

        filteredDataSrc?.forEach(ds => {
            const Data = ds.originalData
            const userCam = liveCamera.user

            const videolink = `https://202.126.209.190:6063/DownloadRecordFile.php?User=${userCam.UserName}&GlobalSessionId=${userCam.SessionId}&FileId=${Data.FileId}`

            window.open(videolink, '_blank')
        })
    }

    return (
        <div className = 'page-container'> 
            <Page title = 'Vehicle Report'>
                <div className = "formField" style = {{ marginBottom: 5 }}>
                    <div style={{ display: 'flex', flexDirection: 'column'}}>
                        <div className = "formField">
                            <SearchFields
                                // selectedVehicle = {VIDS.map(vid => `${vehicles[vid].displayName}`)}
                                onVIDChange = {(newIDs) => setVIDS(newIDs[0] === 'All' ? Object.values(vehicles).map(v => v.vid) : newIDs.map(v => v?.split('@')[1]))}
                                render = {(plate, vehicle) => <VehiclePlateECUTag plate={plate} vehicle={vehicle} />}
                            />

                            <div className = "formField">
                                <span className = "formFieldLabel">Timeframe: </span>

                                <DatePicker.RangePicker
                                    showTime
                                    disabledDate={handleCalendarDisabledDate}
                                    disabled={!Object.keys(vehicles)?.length}
                                    value = {dateValues} 
                                    // value = {[dayJSLocalize(startTime), dayJSLocalize(endTime)]} 
                                    onChange = {(dates) => {
                                        setStartTime(dates[0])
                                        setEndTime(dates[1])
                                    }}
                                    style = {{ width: '380px' }}
                                />
                            </div>

                            <div className = "formField">
                                <span className = "formFieldLabel">Report: </span>

                                <Select
                                    placeholder = {"Select a Report"}
                                    value = {selectedTable && formatTableJSON[selectedTable].title}
                                    onChange = {(newTable) => {
                                        setFilteredDataSrc([])
                                        setSelectedTable(newTable)
                                    }
                                }
                                    style = {{ width: 200 }}
                                >
                                    {
                                        Object.keys(formatTableJSON).sort().map((currKey) =>
                                            <Select.Option key = {currKey} value = {currKey}>
                                                {formatTableJSON[currKey].title}
                                            </Select.Option>
                                        )
                                    }
                                </Select>
                            </div>
                        </div>
                    </div>


                    <div className = "formField">
                        <PrimaryButton
                            loading = {loadingVehicleReportState}
                            disabled = {!(selectedTable && VIDS && VIDS.length)}
                            onClick = {submitFormHandler}
                            style = {{ marginLeft: '15px' }}
                            // color = '#ED5B57'
                            color = '#F58C1E'
                        >
                            Submit
                        </PrimaryButton>

                        <PrimaryButton
                            disabled = {!(filteredDataSrc.length)}
                            onClick = {exportDataHandler}
                            style = {{ marginLeft: '15px' }}
                        >
                            Export
                        </PrimaryButton>
                         
                        {
                            selectedTable === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.MDVR_EVENT
                            &&
                            <PrimaryButton
                                disabled = {!(filteredDataSrc.length)}
                                onClick = {bulkDownloadHDLR}
                                style = {{ marginLeft: '15px' }}
                            >
                                Bulk Download
                            </PrimaryButton>
                        }
                    </div>
                </div>

                {
                    selectedTable === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.GEOFENCE
                    &&
                    <SearchAdvanced setGEOIDS={setGEOIDS} setDurations={setDurations}/>
                }
                {
                    selectedTable === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.VEHICLE_UTILIZATION
                    &&
                    <UtilizationExtras filteredData={filteredDataSrc}/>
                }
                {
                    ((selectedTable === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.REFUEL_REPORT) || (selectedTable === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.THEFT_REPORT) || (selectedTable === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.SUMMARY) || ( selectedTable === REPORT.VEHICLE_REPORT_TABLE_TYPE_KEYS.DAILY_SUMMARY))
                    &&
                    <div style={{ display: 'flex' }}>
                        {/* <div className = "formField">
                            <span className = "formFieldLabel">Estimated Litre per KM (RM): </span>
                            <Input value={litrePerKM} onChange={e => setLitrePerKM(e.target.value)} style={{ width: 200}} />
                        </div> */}
                        <div className = "formField">
                            <span className = "formFieldLabel">Estimated Fuel Price per Litre (RM/L): </span>
                            <Input value={filterPrice} onChange={e => setFilterPrice(e.target.value)} style={{ width: 200}} />
                        </div>
                    </div>
                }

                <TabulatedReport
                    reportJSON = {formatTableJSON}
                    selectedTable = {selectedTable}
                    dataSrc = {filteredDataSrc}
                    loading = {loadingVehicleReportState}
                />
                
            </Page>
        </div>
    )
}

const MemorizedVehicleOverallReport = React.memo(VehicleOverallReport);

export default showSecondarySidebar(false)(MemorizedVehicleOverallReport);